import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { Marke } from './Marke.js'
import { Fuss } from './Fuss.js'
import { Alpen } from './Alpen.js'
import {
  PAKETE,
  preisText,
  merkmaleMitZahlen,
  benutzerText,
  speicherText,
  TESTPHASE_TAGE
} from '@raporto/shared'

/**
 * Die öffentliche Startseite.
 *
 * Die Seite zeigt Raporto wie ein Produktbild: keine Screenshots, keine
 * Menschenfotos, keine erfundenen Kunden. Die Geräte rechts sind deshalb aus
 * HTML und CSS gebaut und bleiben auf Retina-Bildschirmen scharf.
 */

type ModulArt = 'rechnung' | 'offerte' | 'rapport' | 'zahlung' | 'projekt' | 'kunde'
type ZusageArt = 'wolke' | 'geraete' | 'sicherheit' | 'support'
type AktivitaetsArt = 'gut' | 'gold' | 'warnung'

const MODULKARTEN: Array<{ art: ModulArt; titel: string; text: string }> = [
  { art: 'rechnung', titel: 'Rechnungen', text: 'Schnell und korrekt' },
  { art: 'offerte', titel: 'Offerten', text: 'Professionell überzeugen' },
  { art: 'rapport', titel: 'Rapporte', text: 'Einfach erfassen' },
  { art: 'projekt', titel: 'Projekte', text: 'Alles im Blick' },
  { art: 'kunde', titel: 'Kunden', text: 'Zentral verwalten' },
  { art: 'zahlung', titel: 'Zahlungen', text: 'Immer aktuell' }
]

const ZUSAGEN: Array<{ art: ZusageArt; text: string }> = [
  { art: 'wolke', text: 'Überall verfügbar' },
  { art: 'geraete', text: 'Auf allen Geräten' },
  { art: 'sicherheit', text: 'Höchste Datensicherheit' },
  { art: 'support', text: 'Persönlicher Support' }
]

const SEITENLEISTE: string[] = [
  'Übersicht',
  'Rechnungen',
  'Offerten',
  'Rapporte',
  'Projekte',
  'Kunden',
  'Zahlungen',
  'Leistungskatalog',
  'Einstellungen'
]

const KENNZAHLEN: Array<{
  titel: string
  zahl: string
  unter: string
  art?: 'alarm'
}> = [
  { titel: 'Offene Rechnungen', zahl: '12', unter: 'CHF 24’680' },
  { titel: 'Offene Offerten', zahl: '8', unter: 'CHF 18’450' },
  { titel: 'Laufende Projekte', zahl: '6', unter: 'In Bearbeitung' },
  { titel: 'Überfällige Zahlungen', zahl: '3', unter: 'CHF 7’320', art: 'alarm' }
]

const MONATE: Array<{ kurz: string; hoehe: string }> = [
  { kurz: 'Jan', hoehe: '18%' },
  { kurz: 'Feb', hoehe: '26%' },
  { kurz: 'Mär', hoehe: '31%' },
  { kurz: 'Apr', hoehe: '43%' },
  { kurz: 'Mai', hoehe: '48%' },
  { kurz: 'Jun', hoehe: '56%' },
  { kurz: 'Jul', hoehe: '61%' },
  { kurz: 'Aug', hoehe: '68%' },
  { kurz: 'Sep', hoehe: '74%' },
  { kurz: 'Okt', hoehe: '83%' },
  { kurz: 'Nov', hoehe: '91%' },
  { kurz: 'Dez', hoehe: '100%' }
]

const AKTIVITAETEN: Array<{
  zeichen: string
  art: AktivitaetsArt
  text: string
  wann: string
}> = [
  { zeichen: '✓', art: 'gut', text: 'Neuer Rapport-Eintrag', wann: 'Heute, 09:14' },
  { zeichen: '↗', art: 'gold', text: 'Rechnung versendet', wann: 'Gestern, 16:20' },
  { zeichen: '✓', art: 'gut', text: 'Zahlung eingegangen', wann: 'Gestern, 11:03' },
  { zeichen: '•', art: 'warnung', text: 'Projekt aktualisiert', wann: '12. März, 14:21' }
]

const AUFGABEN: Array<{
  zeichen: string
  art: AktivitaetsArt
  text: string
  wann: string
}> = [
  { zeichen: '✓', art: 'gut', text: 'Rapport erfassen', wann: 'Heute' },
  { zeichen: '□', art: 'gut', text: 'Offerte prüfen', wann: 'Heute' },
  { zeichen: '↗', art: 'gold', text: 'Rechnung versenden', wann: 'Morgen' },
  { zeichen: '•', art: 'warnung', text: 'Baustellen-Termin', wann: 'Morgen' }
]

const HANDYREITER: Array<{ zeichen: string; wort: string; aktiv?: boolean }> = [
  { zeichen: '▦', wort: 'Übersicht', aktiv: true },
  { zeichen: '⌂', wort: 'Projekte' },
  { zeichen: '✓', wort: 'Aufgaben' },
  { zeichen: '···', wort: 'Mehr' }
]

type CSSHoehe = CSSProperties & { '--h': string }
type BuehnenStil = CSSProperties & {
  '--rp-neu-skala-breite': string
  '--rp-neu-skala-hoehe': string
}

export function Landing({
  angemeldet,
  aufLogin,
  aufApp,
  aufPreise,
  aufPfad,
  aufBestellen
}: {
  angemeldet: boolean
  aufLogin: () => void
  aufApp: () => void
  aufPreise: () => void
  aufFunktionen: () => void
  aufBranchen: () => void
  /** Ein beliebiger öffentlicher Pfad – der Fussbereich verweist auf sieben. */
  aufPfad: (pfad: string) => void
  /** Ein Paket bestellen – der Knopf auf jeder Paketkarte. */
  aufBestellen: (paket: string) => void
  /** AGB, Datenschutz, Auftragsbearbeitung, Impressum. */
  aufRecht: (id: string) => void
}): JSX.Element {
  return (
    <StartseiteNeu
      angemeldet={angemeldet}
      aufLogin={aufLogin}
      aufApp={aufApp}
      aufPreise={aufPreise}
      aufPfad={aufPfad}
      aufBestellen={aufBestellen}
    />
  )

  const anmelden = (): void => (angemeldet ? aufApp() : aufLogin())
  /*
    Der Paketknopf führt zur Bestellung und nicht zur Anmeldung.

    Vorher landeten beide beim Anmeldeformular. Ein Besucher, der auf «Jetzt
    starten» drückt und nach einem Passwort gefragt wird, das er nie vergeben
    hat, hält die Seite für kaputt – und ist weg.
  */
  const bestellen = (paket: string): void => (angemeldet ? aufApp() : aufBestellen(paket))
  const loginText = angemeldet ? 'Zur App' : 'Login'

  const [geschoben, setGeschoben] = useState(false)
  useEffect(() => {
    const beiRollen = (): void => setGeschoben(window.scrollY > 8)
    beiRollen()
    window.addEventListener('scroll', beiRollen, { passive: true })
    return () => window.removeEventListener('scroll', beiRollen)
  }, [])

  const nachOben = (): void => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="rp-public rp-startseite">
      <header className={geschoben ? 'rp-top rp-geschoben' : 'rp-top'}>
        <button className="rp-marke" type="button" onClick={nachOben} id="btn-marke">
          {/*
            Die farbige Fassung: das Band im Kopf ist hell. Gemessen, nicht
            angenommen – im Stilblatt stand weiter oben noch eine dunkle
            Fassung, die eine spätere Regel längst überschreibt.
          */}
          <Marke groesse={32} mitClaim />
        </button>

        <nav className="rp-nav" aria-label="Hauptnavigation">
          <a href="#produkte">Produkte</a>
          <button type="button" className="rp-nav-knopf" id="btn-nav-preise" onClick={aufPreise}>
            Preise
          </button>
          <a href="#sicherheit">Sicherheit</a>
          <a href="#ueber-uns">Über uns</a>
          <a href="#kontakt">Kontakt</a>
        </nav>

        <div className="rp-top-rechts">
          <span className="rp-suchsymbol" aria-hidden="true">
            <SuchZeichen />
          </span>
          <button className="rp-knopf rp-knopf-hell" id="btn-top-login" onClick={anmelden}>
            {loginText}
          </button>
          <a className="rp-knopf rp-knopf-gold rp-top-produkt" href="#produkte">
            Produktwelt ansehen
            <span className="rp-pfeil" aria-hidden="true">
              →
            </span>
          </a>
        </div>
      </header>

      <main>
        <section className="rp-kopf" id="produktwelt">
          <Alpen />
          <div className="rp-kopf-glanz" aria-hidden="true" />

          <div className="rp-mitte">
            <div className="rp-kopf-raster">
              <div className="rp-kopf-text">
                <p className="rp-augenbraue">EINFACH. TRANSPARENT. EFFIZIENT.</p>
                <h1>
                  Die digitale Betriebszentrale
                  <br />
                  für <span className="rp-gold">Handwerksbetriebe</span>.
                </h1>
                <p className="rp-lead">
                  Raporto vereint Offerten, Rechnungen, Rapporte, Zahlungen und Projekte in einem
                  System – überall verfügbar, auf jedem Gerät.
                </p>

                <div className="rp-kopf-knoepfe">
                  <a
                    className="rp-knopf rp-knopf-gold rp-knopf-gross"
                    id="btn-hero-produktwelt"
                    href="#produkte"
                  >
                    Produktwelt ansehen
                    <span className="rp-pfeil" aria-hidden="true">
                      →
                    </span>
                  </a>
                  <a className="rp-knopf rp-knopf-spiel rp-knopf-gross" href="#produktbild">
                    <span className="rp-spielzeichen" aria-hidden="true">
                      ▶
                    </span>
                    Live erleben
                  </a>
                </div>

                <ul className="rp-haken">
                  <li>
                    <span aria-hidden="true">✓</span> Schnell einsatzbereit
                  </li>
                  <li>
                    <span aria-hidden="true">✓</span> DSGVO-konform
                  </li>
                  <li>
                    <span aria-hidden="true">✓</span> Für die Schweiz entwickelt
                  </li>
                </ul>
              </div>

              <div className="rp-geraete" id="produktbild" aria-hidden="true">
                <p className="rp-notiz rp-notiz-links">
                  Dein Betrieb.
                  <br />
                  Alle Aufgaben.
                  <br />
                  Ein System.
                </p>
                <div className="rp-notebook">
                  <div className="rp-notebook-deckel">
                    <div className="rp-notebook-glas">
                      <div className="rp-bildschirm">
                        {/*
                          Die Seitenleiste zeigt, was die Software wirklich
                          zeigt – und das ist nicht unser Name.

                          Oben steht der Betrieb. Unten, klein, stehen wir.
                          Vorher stand hier bloss «Raporto», und das war
                          doppelt falsch: es war nicht unsere Marke, sondern
                          ein Wort in der Systemschrift, und es behauptete
                          eine Software, die sich selbst in den Vordergrund
                          stellt. Wer das Bild mit dem Bildschirm eines
                          Kollegen vergleicht, soll dasselbe sehen.
                        */}
                        <aside className="rp-bs-seite">
                          <div className="rp-bs-marke">Muster Bau GmbH</div>
                          <div className="rp-bs-nav">
                            {SEITENLEISTE.map((eintrag, index) => (
                              <span key={eintrag} className={index === 0 ? 'aktiv' : undefined}>
                                <i>{index === 0 ? '▦' : '□'}</i>
                                {eintrag}
                              </span>
                            ))}
                          </div>
                          <div className="rp-bs-fuss">
                            <span>powered by</span>
                            <Marke groesse={9} />
                          </div>
                        </aside>

                        <div className="rp-bs-inhalt">
                          <div className="rp-bs-leiste">
                            <span className="rp-bs-suche">
                              <span>⌕</span> Suche …
                            </span>
                            <span className="rp-bs-glocke">⌾</span>
                          </div>

                          <div className="rp-bs-koerper">
                            <div className="rp-bs-kopf">
                              <b>Übersicht</b>
                            </div>

                            <div className="rp-kacheln">
                              {KENNZAHLEN.map((kennzahl) => (
                                <div
                                  className={
                                    kennzahl.art === 'alarm' ? 'rp-kachel alarm' : 'rp-kachel'
                                  }
                                  key={kennzahl.titel}
                                >
                                  <small>{kennzahl.titel}</small>
                                  <b>{kennzahl.zahl}</b>
                                  <i>{kennzahl.unter}</i>
                                </div>
                              ))}
                            </div>

                            <div className="rp-bs-zwei">
                              <div className="rp-bs-kasten">
                                <div className="rp-bs-kasten-kopf">
                                  <b>Umsatzdiagramm</b>
                                  <span className="rp-bs-wahl">2026 ⌄</span>
                                </div>
                                <div className="rp-diagramm">
                                  <div className="rp-achse">
                                    <span>30</span>
                                    <span>15</span>
                                    <span>0</span>
                                  </div>
                                  <div className="rp-balken">
                                    {MONATE.map((monat) => (
                                      <span
                                        key={monat.kurz}
                                        style={{ '--h': monat.hoehe } as CSSHoehe}
                                      >
                                        <i>{monat.kurz}</i>
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              <div className="rp-bs-kasten">
                                <div className="rp-bs-kasten-kopf">
                                  <b>Aktivitäten</b>
                                  <span className="rp-bs-mehr">Alle anzeigen →</span>
                                </div>
                                <div className="rp-aktiv">
                                  {AKTIVITAETEN.map((aktivitaet) => (
                                    <div className="rp-aktiv-zeile" key={aktivitaet.text}>
                                      <span className={`rp-aktiv-zeichen ${aktivitaet.art}`}>
                                        {aktivitaet.zeichen}
                                      </span>
                                      <span>
                                        <b>{aktivitaet.text}</b>
                                        <i>{aktivitaet.wann}</i>
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="rp-notebook-scharnier" />
                  <div className="rp-notebook-fuss" />
                </div>

                <div className="rp-handy">
                  <div className="rp-handy-glas">
                    {/*
                      Auf dem Handy steht die Marke als Zeichen und nicht als
                      Wort in der Systemschrift – es ist derselbe Pfad wie
                      überall sonst, nur klein.
                    */}
                    <div className="rp-handy-kopf">
                      <Marke groesse={11} />
                      <span className="rp-handy-uhr">⌕</span>
                    </div>
                    <div className="rp-handy-einstieg">
                      <h3>Heute im Blick</h3>
                      <p>Ihre wichtigsten Kennzahlen</p>
                    </div>
                    <div className="rp-handy-reiter">
                      <span className="aktiv">Heute</span>
                      <span>Aufgaben</span>
                      <span>Rapporte</span>
                    </div>
                    <div className="rp-handy-zahlen">
                      <span>
                        <i>Offen</i>
                        <b>5</b>
                      </span>
                      <span className="faellig">
                        <i>Fällig</i>
                        <b>3</b>
                      </span>
                      <span className="erledigt">
                        <i>Erledigt</i>
                        <b>12</b>
                      </span>
                    </div>
                    <div className="rp-handy-inhalt">
                      <div className="rp-handy-titel">
                        <h4>Aufgabenliste</h4>
                        <span>Alle anzeigen →</span>
                      </div>
                      {AUFGABEN.map((aufgabe) => (
                        <div className="rp-handy-zeile" key={aufgabe.text}>
                          <span className={`rp-aktiv-zeichen ${aufgabe.art}`}>
                            {aufgabe.zeichen}
                          </span>
                          <span className="rp-handy-wort">
                            <b>{aufgabe.text}</b>
                            <i>{aufgabe.wann}</i>
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="rp-handy-fuss">
                      {HANDYREITER.map((reiter) => (
                        <span key={reiter.wort} className={reiter.aktiv ? 'aktiv' : undefined}>
                          <i>{reiter.zeichen}</i>
                          {reiter.wort}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="rp-notiz rp-notiz-rechts">
                  Auch unterwegs.
                  <br />
                  Immer dabei.
                </p>
              </div>
            </div>
          </div>

          <aside className="rp-randspalte" aria-hidden="true">
            <span className="rp-randspalte-wort">
              Handwerk.
              <br />
              Digital.
              <br />
              Stärker morgen.
            </span>
            <span className="rp-randspalte-strich" />
            <span className="rp-randspalte-schwung">
              Mehr möglich
              <br />
              machen.
            </span>
          </aside>
        </section>

        <section className="rp-bereiche" id="produkte">
          <div className="rp-mitte">
            <div className="rp-bereich-raster" id="modulliste">
              {MODULKARTEN.map((modul) => (
                <article className="rp-bereich" key={modul.titel}>
                  <span className="rp-bereich-zeichen" aria-hidden="true">
                    <ModulZeichen art={modul.art} />
                  </span>
                  <div>
                    <b>{modul.titel}</b>
                    <span>{modul.text}</span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="rp-abschnitt rp-pakete-block" id="pakete">
          <div className="rp-mitte">
            <div className="rp-pakete-raster">
              <div className="rp-pakete-text">
                <p className="rp-augenbraue">PAKETE FÜR JEDEN BEDARF</p>
                <h2>
                  Einfach das
                  <br />
                  passende Paket.
                </h2>
                <p className="rp-lead">
                  Transparente Preise. Volle Flexibilität. Raporto wächst mit Ihrem Betrieb.
                </p>
                <ul className="rp-haken">
                  <li>
                    <span aria-hidden="true">✓</span> Keine versteckten Kosten
                  </li>
                  <li>
                    <span aria-hidden="true">✓</span> Jederzeit wechseln
                  </li>
                </ul>
              </div>

              <div className="rp-pakete" id="paketliste">
                {PAKETE.map((paket) => (
                  <article
                    className={paket.empfohlen ? 'rp-paket empfohlen' : 'rp-paket'}
                    key={paket.id}
                    id={`paket-${paket.id}`}
                  >
                    {paket.empfohlen && <span className="rp-paket-fahne">BELIEBTESTE WAHL</span>}
                    <h3>{paket.name}</h3>
                    <p className="rp-paket-wen">{paket.fuerWen}</p>
                    <p className="rp-paket-preis">
                      <span className="rp-paket-waehrung">CHF</span>
                      <b>{preisText(paket.preisRp)}</b>
                      <span className="rp-paket-takt">/ Monat</span>
                    </p>
                    <ul className="rp-paket-liste">
                      {merkmaleMitZahlen(paket)
                        .slice(0, 3)
                        .map((merkmal) => (
                          <li key={merkmal}>
                            <span className="rp-paket-haken" aria-hidden="true">
                              ✓
                            </span>
                            <span className="rp-paket-merkmal">{merkmal}</span>
                          </li>
                        ))}
                    </ul>
                    <button
                      className={paket.empfohlen ? 'rp-knopf rp-knopf-gold' : 'rp-knopf'}
                      id={`btn-paket-${paket.id}`}
                      onClick={() => bestellen(paket.id)}
                    >
                      {angemeldet ? 'Zur App' : `${paket.name} wählen`}
                    </button>
                  </article>
                ))}
              </div>

              <aside className="rp-zusagen" id="sicherheit">
                {ZUSAGEN.map((zusage) => (
                  <div className="rp-zusage" key={zusage.text}>
                    <span aria-hidden="true">
                      <ZusagenZeichen art={zusage.art} />
                    </span>
                    {zusage.text}
                  </div>
                ))}
                <span className="rp-zusagen-linie" aria-hidden="true" />
                <p className="rp-zusagen-satz">STARKE BETRIEBE BAUEN AUF KLARE PROZESSE.</p>
              </aside>
            </div>
          </div>
        </section>
      </main>

      <footer className="rp-fuss rp-fuss-landung" id="kontakt">
        <span id="ueber-uns">Raporto · Schweizer Cloud-Software für Handwerksbetriebe</span>
        <span className="rp-fuss-mitte">
          <a href="#produkte">Produkte</a>
          <button type="button" className="rp-textknopf" onClick={aufPreise}>
            Preise
          </button>
          <a href="#sicherheit">Sicherheit</a>
        </span>
        <span>Kontakt über den Raporto-Support</span>
      </footer>
    </div>
  )
}

function SuchZeichen(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M15.5 15.5 21 21" />
    </svg>
  )
}

function ModulZeichen({ art }: { art: ModulArt }): JSX.Element {
  if (art === 'kunde') {
    return (
      <svg viewBox="0 0 32 32" focusable="false" aria-hidden="true">
        <circle cx="16" cy="10" r="5" />
        <path d="M7 26c1.2-5.1 4.2-8 9-8s7.8 2.9 9 8" />
        <path d="M21.5 20.5 25 24l-3.5 3.5" />
      </svg>
    )
  }

  if (art === 'zahlung') {
    return (
      <svg viewBox="0 0 32 32" focusable="false" aria-hidden="true">
        <rect x="5" y="8" width="22" height="16" rx="2.5" />
        <path d="M5 13h22" />
        <path d="M10 20h7" />
        <path d="M22 19.5h2.5" />
      </svg>
    )
  }

  if (art === 'projekt') {
    return (
      <svg viewBox="0 0 32 32" focusable="false" aria-hidden="true">
        <path d="M7 26V8h7l2 3h9v15H7Z" />
        <path d="M11 17h10" />
        <path d="M11 21h7" />
      </svg>
    )
  }

  if (art === 'rapport') {
    return (
      <svg viewBox="0 0 32 32" focusable="false" aria-hidden="true">
        <path d="M8 5h12l4 4v18H8V5Z" />
        <path d="M20 5v5h5" />
        <path d="M12 18h8" />
        <path d="m12 23 2 2 5-6" />
      </svg>
    )
  }

  if (art === 'offerte') {
    return (
      <svg viewBox="0 0 32 32" focusable="false" aria-hidden="true">
        <path d="M8 5h12l4 4v18H8V5Z" />
        <path d="M20 5v5h5" />
        <path d="M12 15h9" />
        <path d="M12 19h7" />
        <circle cx="21" cy="23" r="2.5" />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 32 32" focusable="false" aria-hidden="true">
      <path d="M8 5h12l4 4v18H8V5Z" />
      <path d="M20 5v5h5" />
      <path d="M12 15h8" />
      <path d="M12 19h8" />
      <path d="M12 23h5" />
    </svg>
  )
}

function ZusagenZeichen({ art }: { art: ZusageArt }): JSX.Element {
  if (art === 'geraete') {
    return (
      <svg viewBox="0 0 28 28" focusable="false" aria-hidden="true">
        <rect x="4" y="6" width="14" height="12" rx="1.5" />
        <path d="M8 22h8" />
        <path d="M12 18v4" />
        <rect x="20" y="9" width="4" height="11" rx="1" />
      </svg>
    )
  }

  if (art === 'sicherheit') {
    return (
      <svg viewBox="0 0 28 28" focusable="false" aria-hidden="true">
        <rect x="7" y="12" width="14" height="10" rx="2" />
        <path d="M10 12V9a4 4 0 0 1 8 0v3" />
        <path d="M14 16v3" />
      </svg>
    )
  }

  if (art === 'support') {
    return (
      <svg viewBox="0 0 28 28" focusable="false" aria-hidden="true">
        <path d="M6 16v-2a8 8 0 0 1 16 0v2" />
        <path d="M6 16v4h4v-6H6v2Z" />
        <path d="M22 16v4h-4v-6h4v2Z" />
        <path d="M18 22h-3" />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 28 28" focusable="false" aria-hidden="true">
      <path d="M9 21h10.5a5 5 0 0 0 .3-10 7 7 0 0 0-13.5 1.8A4.2 4.2 0 0 0 9 21Z" />
    </svg>
  )
}

type NeuFunktionsArt =
  | 'auftrag'
  | 'kalender'
  | 'kunde'
  | 'offerte'
  | 'rechnung'
  | 'team'
  | 'datei'
  | 'zahlung'
  | 'benutzer'
  | 'einstellung'
  | 'los'

/*
  Nur Beschriftungen – Reihenfolge, Zeichen und Farben bleiben, wie sie sind.

  Drei der sieben Kacheln nannten Bereiche, die es nicht gibt: «Kalender»,
  «Team» und «Dateien». «Dateien – Alles an einem Ort. Sicher gespeichert.»
  widersprach zusätzlich den AGB, in denen ausdrücklich steht, dass Raporto
  keine allgemeine Ablage ist.

  Hier stehen jetzt Bereiche, die ein Betrieb nach der Anmeldung in der
  Seitenleiste wirklich findet. Jeder neue Text ist höchstens so lang wie der
  alte, damit keine Kachel wächst.
*/
const NEUE_FUNKTIONEN: Array<{ art: NeuFunktionsArt; titel: string; text: string }> = [
  { art: 'auftrag', titel: 'Projekte', text: 'Baustelle, Kunde und Team.' },
  { art: 'kalender', titel: 'Rapporte', text: 'Stunden und Material.' },
  { art: 'kunde', titel: 'Kunden', text: 'Adressen, Historie, offener Betrag.' },
  { art: 'offerte', titel: 'Offerten', text: 'Mit Gültigkeit und Nummer.' },
  { art: 'rechnung', titel: 'Rechnungen', text: 'Mit Schweizer QR-Zahlteil.' },
  { art: 'team', titel: 'Monteure', text: 'Am Handy, auf Wunsch ohne Preise.' },
  { art: 'datei', titel: 'Leistungen', text: 'Positionen mit Preis und MWST.' }
]

const NEUE_SEITENLEISTE = [
  'Übersicht',
  'Aufträge',
  'Kalender',
  'Kunden',
  'Team',
  'Offerten',
  'Rechnungen',
  'Daten',
  'Dateien',
  'Einstellungen'
]

const NEUE_KENNZAHLEN: Array<{
  art: NeuFunktionsArt
  zahl: string
  titel: string
  text: string
}> = [
  { art: 'auftrag', zahl: '12', titel: 'Offene Aufträge', text: '+4 heute' },
  { art: 'kalender', zahl: '8', titel: 'Einsätze heute', text: 'gut verteilt' },
  { art: 'offerte', zahl: '4', titel: 'Offerten in Arbeit', text: 'zur Prüfung' },
  { art: 'rechnung', zahl: 'CHF 12’480', titel: 'Umsatz', text: 'diesen Monat' }
]

const NEUER_TAGESPLAN: Array<{ zeit: string; titel: string; status: string }> = [
  { zeit: '08:00', titel: 'Büroreinigung - Müller AG', status: 'In Arbeit' },
  { zeit: '10:30', titel: 'Gartenpflege - Schmidt', status: 'Geplant' },
  { zeit: '12:00', titel: 'Montage - Kellerbau', status: 'Geplant' },
  { zeit: '15:30', titel: 'Endreinigung - Stadtwerk', status: 'Offen' }
]

/*
  Hier stand bis zuletzt die Zusage, es brauche keine Karte. Sie stimmt nicht
  mehr: die Testphase ist gratis, danach wird ab der hinterlegten Karte
  abgebucht.
  Der Satz musste vor Stripe weg, nicht nachher – ein Versprechen, das man
  zurücknimmt, kostet mehr als eines, das man nie gegeben hat. «Jederzeit
  kündbar» stimmt heute und nachher.
*/
const NEUE_VERTRAUEN = [
  'Jederzeit kündbar',
  'Im Browser und am Handy',
  /* Die Zahl kommt aus @raporto/shared und stand hier getippt – an der ersten
     Stelle, die ein Besucher liest, und der letzten, die jemand nachführt. */
  `${TESTPHASE_TAGE} Tage testen`
]

const NEUE_SCHRITTE: Array<{
  art: NeuFunktionsArt
  zahl: string
  titel: string
  text: string
}> = [
  {
    art: 'benutzer',
    zahl: '1',
    titel: 'Konto erstellen',
    text: 'In wenigen Minuten startklar.'
  },
  {
    art: 'einstellung',
    zahl: '2',
    titel: 'Betrieb einrichten',
    text: 'Daten, Team und Einstellungen anpassen.'
  },
  {
    art: 'los',
    zahl: '3',
    titel: 'Loslegen',
    text: 'Aufträge verwalten und durchstarten.'
  }
]

const NEUE_MONATE: Array<{ name: string; wert: string }> = [
  { name: 'Jan', wert: '24%' },
  { name: 'Feb', wert: '34%' },
  { name: 'Mär', wert: '43%' },
  { name: 'Apr', wert: '52%' },
  { name: 'Mai', wert: '61%' },
  { name: 'Jun', wert: '68%' },
  { name: 'Jul', wert: '74%' }
]

type NeueBalkenStil = CSSProperties & { '--wert': string }

/*
  Hier stand eine eigene Namensliste der Startseite: starter → Basic,
  team → Premium. Zweimal hat dieselbe Konstruktion dieselbe Wirkung gehabt –
  die Startseite nannte die Pakete anders als Preisseite, Bestellung und
  Rechnung, und niemand merkt es, bis ein Kunde nach «Premium» fragt und im
  Abo «Team» steht.

  Die Namen kommen jetzt aus packages/shared. Eine Stelle, drei Zeilen; wer
  sie ändert, ändert sie überall.
*/
function StartseiteNeu({
  angemeldet,
  aufLogin,
  aufApp,
  aufPreise,
  aufPfad,
  aufBestellen
}: {
  angemeldet: boolean
  aufLogin: () => void
  aufApp: () => void
  aufPreise: () => void
  aufPfad: (pfad: string) => void
  aufBestellen: (paket: string) => void
}): JSX.Element {
  const [geschoben, setGeschoben] = useState(false)
  const [buehnenSkalierung, setBuehnenSkalierung] = useState({ breite: 1, hoehe: 1 })
  const anmelden = (): void => (angemeldet ? aufApp() : aufLogin())
  const starten = (): void => (angemeldet ? aufApp() : aufBestellen('pro'))

  useEffect(() => {
    const beiRollen = (): void => setGeschoben(window.scrollY > 10)
    beiRollen()
    window.addEventListener('scroll', beiRollen, { passive: true })
    return () => window.removeEventListener('scroll', beiRollen)
  }, [])

  useLayoutEffect(() => {
    const entwurfBreite = 1672
    const entwurfHoehe = 941

    const beiGroesse = (): void => {
      if (window.innerWidth < 1181) {
        setBuehnenSkalierung({ breite: 1, hoehe: 1 })
        return
      }

      setBuehnenSkalierung({
        breite: window.innerWidth / entwurfBreite,
        hoehe: window.innerHeight / entwurfHoehe
      })
    }

    beiGroesse()
    window.addEventListener('resize', beiGroesse)
    return () => window.removeEventListener('resize', beiGroesse)
  }, [])

  /*
    Bewegung und Leuchten.

    Das Attribut data-bewegt schaltet in public.css die ganze Bewegungsebene
    ein – und es wird erst hier gesetzt, nach dem ersten Rendern. Das ist
    Absicht: Ohne JavaScript bleibt die Seite vollständig sichtbar, statt aus
    lauter unsichtbaren Kästen zu bestehen. Der Fehler kostet sonst die ganze
    Seite, und er fällt niemandem auf, der ihn gebaut hat.

    Zwei Dinge passieren hier:

      1. Ein Beobachter blendet jeden Abschnitt ein, sobald er ins Bild
         kommt. Danach hängt er sich ab – ein Element, das schon da ist,
         braucht nicht weiter beobachtet zu werden.
      2. Der Zeiger verschiebt das Produktbild um ein paar Pixel. Gerechnet
         wird in -1 … 1, damit im CSS nur noch eine Multiplikation steht.

    Wer im System «weniger Bewegung» eingestellt hat, bekommt gar nichts
    davon: dann wird das Attribut nicht gesetzt.
  */
  const wurzel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const knoten = wurzel.current
    if (!knoten) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    knoten.setAttribute('data-bewegt', 'an')

    const teile = Array.from(knoten.querySelectorAll<HTMLElement>('.rp-auf'))
    let beobachter: IntersectionObserver | null = null

    if ('IntersectionObserver' in window) {
      beobachter = new IntersectionObserver(
        (eintraege, selbst) => {
          for (const e of eintraege) {
            if (!e.isIntersecting) continue
            e.target.classList.add('rp-da')
            selbst.unobserve(e.target)
          }
        },
        // 12 % statt 0: Der Abschnitt soll eingeblendet sein, wenn man ihn
        // liest, und nicht erst anfangen, wenn er schon halb vorbei ist.
        { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
      )
      for (const t of teile) beobachter.observe(t)
    } else {
      for (const t of teile) t.classList.add('rp-da')
    }

    const beiZeiger = (e: PointerEvent): void => {
      const x = (e.clientX / window.innerWidth) * 2 - 1
      const y = (e.clientY / window.innerHeight) * 2 - 1
      knoten.style.setProperty('--rp-mx', x.toFixed(3))
      knoten.style.setProperty('--rp-my', y.toFixed(3))
    }

    const zeigerVorhanden = window.matchMedia('(hover: hover)').matches
    if (zeigerVorhanden) window.addEventListener('pointermove', beiZeiger, { passive: true })

    return () => {
      beobachter?.disconnect()
      if (zeigerVorhanden) window.removeEventListener('pointermove', beiZeiger)
    }
  }, [])

  const nachOben = (): void => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const buehnenStil = {
    '--rp-neu-skala-breite': buehnenSkalierung.breite.toFixed(5),
    '--rp-neu-skala-hoehe': buehnenSkalierung.hoehe.toFixed(5)
  } as BuehnenStil

  return (
    <>
      <div className="rp-public rp-neue-startseite" ref={wurzel}>
      <div className="rp-neu-buehne" style={buehnenStil}>
        <section className="rp-neu-kopf" id="start">
          <div className="rp-neu-gitter" aria-hidden="true" />
          <div className="rp-neu-strahlen" aria-hidden="true" />
          <div className="rp-neu-glanz rp-neu-glanz-links" aria-hidden="true" />
          <div className="rp-neu-glanz rp-neu-glanz-rechts" aria-hidden="true" />

          <header className={geschoben ? 'rp-neu-leiste rp-geschoben' : 'rp-neu-leiste'}>
            <button className="rp-neu-marke" type="button" onClick={nachOben} id="btn-marke">
              <Marke groesse={42} mitClaim />
            </button>

            <nav className="rp-neu-nav" aria-label="Hauptnavigation">
              <a href="/funktionen">So funktioniert’s</a>
              <a href="/branchen">Branchen</a>
              <a href="/preise">Pakete</a>
              <a href="/faq">FAQ</a>
            </nav>

            <div className="rp-neu-aktionen">
              <button type="button" className="rp-neu-login" id="btn-top-login" onClick={anmelden}>
                {angemeldet ? 'Zur App' : 'Anmelden'}
              </button>
              <button
                type="button"
                className="rp-neu-knopf rp-neu-knopf-hell"
                id="btn-top-kostenlos"
                onClick={starten}
              >
                {angemeldet ? 'Zur App' : 'Kostenlos starten'}
                <span aria-hidden="true">→</span>
              </button>
            </div>
          </header>

          <div className="rp-neu-held">
            <div className="rp-neu-text">
              <span className="rp-neu-pill">Die digitale Betriebszentrale</span>
              <h1>
                Von der Offerte
                <br />
                <span>bis zur Rechnung.</span>
              </h1>
              <p>
                Raporto hält zusammen, was im Alltag sonst verteilt ist: Kunden, Termine,
                Leistungen, Rapporte und Rechnungen.
              </p>

              <div className="rp-neu-heldknoepfe">
                <button
                  type="button"
                  className="rp-neu-knopf rp-neu-knopf-hell"
                  id="btn-hero-kostenlos"
                  onClick={starten}
                >
                  Kostenlos starten
                  <span aria-hidden="true">→</span>
                </button>
                <a className="rp-neu-knopf rp-neu-knopf-dunkel" href="/funktionen">
                  So funktioniert’s
                </a>
              </div>

              <ul className="rp-neu-vertrauen" aria-label="Vorteile">
                {NEUE_VERTRAUEN.map((punkt) => (
                  <li key={punkt}>
                    <span aria-hidden="true">✓</span>
                    {punkt}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rp-neu-produkt" aria-label="Raporto Produktvorschau">
              <span className="rp-neu-rohbild rp-neu-rohbild-offerte" aria-hidden="true" />
              <span className="rp-neu-rohbild rp-neu-rohbild-zahlung" aria-hidden="true" />
              <span className="rp-neu-rohbild rp-neu-rohbild-team" aria-hidden="true" />
              <span className="rp-neu-rohbild rp-neu-rohbild-rechnung" aria-hidden="true" />

              <p className="rp-neu-schrift rp-neu-schrift-links">
                Mehr Zeit
                <br />
                für das Wesentliche.
              </p>
              <p className="rp-neu-schrift rp-neu-schrift-rechts">Überall dabei.</p>
              <p className="rp-neu-seitenwort">
                <span>Planen</span>
                <span>Organisieren</span>
                <span>Erledigen</span>
                <span>Wachsen</span>
              </p>

              <span
                className="rp-neu-rohbild rp-neu-rohbild-laptop"
                role="img"
                aria-label="Raporto Übersicht auf einem Laptop"
              />
              <span
                className="rp-neu-rohbild rp-neu-rohbild-handy"
                role="img"
                aria-label="Raporto Übersicht auf einem Handy"
              />
              <p className="rp-neu-sockeltext">Ein System. Alle Möglichkeiten.</p>
              <p className="rp-neu-morgen">Raporto. Heute für morgen.</p>
            </div>
          </div>
        </section>

        <main>
          <section className="rp-neu-funktionen" id="funktionen">
            <div className="rp-neu-funktionskopf rp-auf">
              <p>So funktioniert's</p>
              <h2>Alles in einem System.</h2>
              <span>
                Raporto verbindet Kunden, Offerten und Rechnungen – vom ersten Kontakt bis zum
                Abschluss.
              </span>
            </div>
            <div className="rp-neu-funktionskarten">
              {NEUE_FUNKTIONEN.map((funktion) => (
                <article className="rp-neu-funktionskarte rp-auf" key={funktion.titel}>
                  <span className={`rp-neu-ikon rp-neu-ikon-${funktion.art}`} aria-hidden="true">
                    <NeuZeichen art={funktion.art} />
                  </span>
                  <h3>{funktion.titel}</h3>
                  <p>{funktion.text}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="rp-neu-ablauf" id="branchen">
            <div className="rp-neu-ablauftext rp-auf">
              <p>So einfach geht's</p>
              <h2>
                So arbeitet <span>Raporto.</span>
              </h2>
              <small>
                In wenigen Schritten zu mehr Übersicht, weniger Aufwand und einem organisierten
                Betrieb.
              </small>
            </div>
            <div className="rp-neu-schritte rp-auf">
              <a className="rp-neu-rundknopf" href="#preise" aria-label="Zu den Preisen">
                →
              </a>
              {NEUE_SCHRITTE.map((schritt, index) => [
                <article className="rp-neu-schritt rp-auf" key={`${schritt.zahl}-schritt`}>
                  <span className="rp-neu-schrittzahl">{schritt.zahl}</span>
                  <span className={`rp-neu-schrittikon rp-neu-ikon-${schritt.art}`}>
                    <NeuZeichen art={schritt.art} />
                  </span>
                  <h3>{schritt.titel}</h3>
                  <p>{schritt.text}</p>
                </article>,
                index < NEUE_SCHRITTE.length - 1 ? (
                  <span
                    className="rp-neu-schrittpfeil"
                    key={`${schritt.zahl}-pfeil`}
                    aria-hidden="true"
                  >
                    →
                  </span>
                ) : null
              ])}
            </div>
            <p className="rp-neu-schrift rp-neu-startschrift">
              Einfach starten.
              <br />
              Grosses erreichen.
            </p>
            <div className="rp-neu-kristall" aria-hidden="true" />
          </section>

          <section className="rp-neu-unten rp-auf" id="preise">
            <article className="rp-neu-stimme" id="kundenstimmen">
              <p className="rp-neu-label">Kundenstimme</p>
              <blockquote>
                Raporto unterstützt uns dort, wo im Alltag viele Informationen zusammenlaufen: bei
                Offerten, Einsätzen und Rechnungen. Die Abläufe bleiben nachvollziehbar, ohne dass
                die Arbeit komplizierter wird.
              </blockquote>
              <b>AV Parkett</b>
              <span>Boden- und Parkettarbeiten mit Anspruch – aus Rotkreuz.</span>
              <div className="rp-neu-av" aria-hidden="true">
                <strong>AV</strong>
                <small>Parkett</small>
              </div>
              <em>Seit 2026 mit Raporto im Einsatz.</em>
            </article>

            {/*
              Die Pakete stehen hier, die Preise auf der Preisseite.

              Hier lagen einmal dieselben Karten mit eigenen Merkmalslisten:
              «100 GB Speicher», «Unbegrenzte Benutzer» – während die
              Preisseite 50 GB und zwanzig nannte. Zwei Listen über dieselbe
              Sache driften auseinander, und diese taten es. Die Merkmale
              kommen jetzt aus den Paketen, so wie überall sonst.

              Der Betrag selbst steht nicht mehr auf der Karte. Er gehört
              neben den Vergleich, der ihn einordnet –
              eine nackte Zahl auf der Startseite beantwortet die Frage «ist
              das viel» nicht, sie stellt sie.
            */}
            <article className="rp-neu-preistext">
              <p className="rp-neu-label">Die Pakete</p>
              <h2>Wachsen mit Raporto.</h2>
              <span>
                Drei Pakete, ohne Mindestlaufzeit und ohne Einrichtungsgebühr. Der Vergleich auf
                der Preisseite zeigt Zeile für Zeile, worin sie sich unterscheiden.
              </span>
            </article>

            <div className="rp-neu-pakete">
              {PAKETE.map((paket) => (
                <article
                  className={paket.empfohlen ? 'rp-neu-paket empfohlen' : 'rp-neu-paket'}
                  key={paket.id}
                  id={`paket-${paket.id}`}
                >
                  {paket.empfohlen && <span className="rp-neu-paketband">Beliebt</span>}
                  <h3>{paket.name}</h3>
                  <p>{paket.fuerWen}</p>
                  {/*
                    Drei Zeilen: Benutzer, ein Merkmal, Speicher.

                    Nicht einfach die ersten drei. Benutzer und Speicher sind
                    die beiden Zahlen, die jemand zwischen den Karten
                    vergleicht; ein blosses slice(0, 3) hätte den Speicher
                    abgeschnitten, und dann stünde die Angabe, um die es hier
                    geht, ausgerechnet nicht da.
                  */}
                  <ul>
                    {[benutzerText(paket), paket.merkmale[0] ?? '', speicherText(paket)]
                      .filter(Boolean)
                      .map((merkmal) => (
                        <li key={merkmal}>
                          <span aria-hidden="true">✓</span>
                          {/*
                            Eigenes Element, nicht nackter Text: in einer
                            Flex-Zeile bekommt ein Textknoten min-width auto
                            und bricht deshalb nicht um – «Rapporte am Handy,
                            auch ohne Ne» stand so über den Kartenrand hinaus.
                          */}
                          <em>{merkmal}</em>
                        </li>
                      ))}
                  </ul>
                  {/*
                    «Zur App» stand hier und war für den Besucher falsch: er
                    hat noch keine App. Der Knopf führt dorthin, wo die Frage
                    beantwortet wird, die er gerade hat – was kostet das, und
                    welches Paket brauche ich.
                  */}
                  <button
                    type="button"
                    className="rp-neu-paketknopf"
                    id={`btn-paket-${paket.id}`}
                    onClick={aufPreise}
                  >
                    Paket wählen
                  </button>
                </article>
              ))}
            </div>
          </section>
        </main>

      </div>
      </div>

      {/*
        Der Abschlussbereich steht NEBEN der Startseite, nicht in ihr.

        Ab 1181 Pixeln trägt .rp-neue-startseite `height: 100vh` und
        `overflow: hidden` – die ganze obere Komposition ist auf einen
        Bildschirm gerechnet. Ein Fussbereich von 780 Pixeln darin wäre
        abgeschnitten, und ihn sichtbar zu machen hiesse, das overflow
        aufzugeben und damit die Bildwirkung oben zu zerlegen.

        Als Geschwister bleibt oben alles, wie Codex es hinterlassen hat, und
        die Seite scrollt genau einmal: von der Bühne in den Fuss.
      */}
      <Fuss aufPfad={aufPfad} />
    </>
  )
}

function LaptopNeu(): JSX.Element {
  return (
    <div className="rp-neu-laptop" aria-hidden="true">
      <div className="rp-neu-laptopdeckel">
        <div className="rp-neu-bildschirm">
          <aside>
            <div className="rp-neu-mini-logo">
              <Marke groesse={19} />
            </div>
            {NEUE_SEITENLEISTE.map((eintrag, index) => (
              <span key={eintrag} className={index === 0 ? 'aktiv' : undefined}>
                <i>{index === 0 ? '●' : '○'}</i>
                {eintrag}
              </span>
            ))}
          </aside>

          <div className="rp-neu-app">
            <div className="rp-neu-appleiste">
              <span className="rp-neu-suchfeld">
                <NeuSuchZeichen /> Suche...
              </span>
              <span className="rp-neu-appsymbol alarm" aria-hidden="true" />
              <span className="rp-neu-appsymbol rad" aria-hidden="true" />
            </div>

            <div className="rp-neu-appkopf">
              <div>
                <h3>Guten Morgen!</h3>
                <p>Dein Betrieb läuft rund.</p>
              </div>
              <span>
                Heute
                <br />
                Di, 17. Juni 2025
              </span>
            </div>

            <div className="rp-neu-kennzahlen">
              {NEUE_KENNZAHLEN.map((kennzahl) => (
                <article key={kennzahl.titel}>
                  <span className={`rp-neu-ikon rp-neu-ikon-${kennzahl.art}`}>
                    <NeuZeichen art={kennzahl.art} />
                  </span>
                  <b>{kennzahl.zahl}</b>
                  <small>{kennzahl.titel}</small>
                  <em>{kennzahl.text}</em>
                </article>
              ))}
            </div>

            <div className="rp-neu-appgitter">
              <article className="rp-neu-plan">
                <h4>Tagesübersicht</h4>
                {NEUER_TAGESPLAN.map((eintrag) => (
                  <div key={eintrag.zeit}>
                    <b>{eintrag.zeit}</b>
                    <span>{eintrag.titel}</span>
                    <i>{eintrag.status}</i>
                  </div>
                ))}
              </article>
              <article className="rp-neu-karte">
                <div className="rp-neu-kartenbild">
                  <span />
                  <span />
                  <span />
                </div>
                <h4>Einsätze heute</h4>
                <p>Alle Teams sauber koordiniert.</p>
              </article>
            </div>
          </div>
        </div>
      </div>
      <div className="rp-neu-laptopfuss" />
    </div>
  )
}

function HandyNeu(): JSX.Element {
  return (
    <div className="rp-neu-handy" aria-hidden="true">
      <div className="rp-neu-notch" />
      <div className="rp-neu-handyglas">
        <div className="rp-neu-handykopf">
          <Marke groesse={19} />
          <span className="rp-neu-menulinien" aria-hidden="true" />
        </div>
        <h3>Hallo!</h3>
        <p>Hier ist dein heutiger Überblick.</p>
        <div className="rp-neu-handykarten">
          <article>
            <span>8</span>
            <b>Einsätze heute</b>
            <NeuZeichen art="kalender" />
          </article>
          <article>
            <span>2</span>
            <b>Neue Nachrichten</b>
            <NeuZeichen art="auftrag" />
          </article>
        </div>
        <div className="rp-neu-handyfuss">
          <span className="aktiv">Übersicht</span>
          <span>Aufträge</span>
          <span>Team</span>
          <span>Mehr</span>
        </div>
      </div>
    </div>
  )
}

function SchwebendeKarte({
  klasse,
  art,
  titel,
  text,
  betrag,
  fuss
}: {
  klasse: string
  art: NeuFunktionsArt
  titel: string
  text: string
  betrag: string
  fuss?: string
}): JSX.Element {
  return (
    <article className={`rp-neu-schwebe ${klasse}`}>
      <span className={`rp-neu-ikon rp-neu-ikon-${art}`} aria-hidden="true">
        <NeuZeichen art={art} />
      </span>
      <div>
        <b>{titel}</b>
        {text && <small>{text}</small>}
        {betrag && <strong>{betrag}</strong>}
        {fuss && <em>{fuss}</em>}
      </div>
    </article>
  )
}

function NeuSuchZeichen(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
      <circle cx="10.5" cy="10.5" r="6.2" />
      <path d="m15.2 15.2 4.8 4.8" />
    </svg>
  )
}

function NeuZeichen({ art }: { art: NeuFunktionsArt }): JSX.Element {
  if (art === 'kalender') {
    return (
      <svg viewBox="0 0 28 28" focusable="false" aria-hidden="true">
        <rect x="6" y="7" width="16" height="15" rx="3" />
        <path d="M9 5v4M19 5v4M6 12h16M10 16h3M15 16h3M10 19h3" />
      </svg>
    )
  }

  if (art === 'kunde' || art === 'team' || art === 'benutzer') {
    return (
      <svg viewBox="0 0 28 28" focusable="false" aria-hidden="true">
        <circle cx="11" cy="10" r="4" />
        <path d="M4.5 22c.9-4.4 3.1-6.6 6.5-6.6s5.7 2.2 6.6 6.6" />
        <circle cx="19" cy="11" r="3" />
        <path d="M17.4 16.2c2.7.2 4.5 2 5.1 5.8" />
      </svg>
    )
  }

  if (art === 'rechnung' || art === 'offerte' || art === 'datei') {
    return (
      <svg viewBox="0 0 28 28" focusable="false" aria-hidden="true">
        <path d="M8 4h8l4 4v16H8V4Z" />
        <path d="M16 4v5h5M11 14h7M11 18h7M11 22h4" />
      </svg>
    )
  }

  if (art === 'zahlung') {
    return (
      <svg viewBox="0 0 28 28" focusable="false" aria-hidden="true">
        <circle cx="14" cy="14" r="9" />
        <path d="m10 14 3 3 6-7" />
      </svg>
    )
  }

  if (art === 'einstellung') {
    return (
      <svg viewBox="0 0 28 28" focusable="false" aria-hidden="true">
        <circle cx="14" cy="14" r="3.5" />
        <path d="M14 4v3M14 21v3M6.9 6.9l2.1 2.1M19 19l2.1 2.1M4 14h3M21 14h3M6.9 21.1 9 19M19 9l2.1-2.1" />
      </svg>
    )
  }

  if (art === 'los') {
    return (
      <svg viewBox="0 0 28 28" focusable="false" aria-hidden="true">
        <path d="M7 4v20l16-10L7 4Z" />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 28 28" focusable="false" aria-hidden="true">
      <path d="M5 8h7l2 3h9v11H5V8Z" />
      <path d="M8 16h11" />
    </svg>
  )
}
